// VERSÃO 16.6 ELITE SHIELDED - ESTABILIDADE TOTAL & SINCRO MASTER
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { UserExecutionEngine } from '../../api/UserExecutionEngine'; 
import { LeadExecutionEngine } from '../../api/LeadExecutionEngine';
import { OrderExecutionEngine } from '../../api/OrderExecutionEngine'; 
import { 
    FiGrid, FiUsers, FiSettings, FiLogOut, 
    FiPlusCircle, FiClock, FiGlobe, FiLink,
    FiCode, FiShield, FiCheckCircle, FiImage,
    FiTarget, FiActivity, FiAward, FiBox, FiTruck, FiArchive, FiUserPlus,
    FiList, FiDatabase, FiLock, FiUnlock
} from 'react-icons/fi';

const navGroups = [
    {
        category: 'Pedidos',
        items: [
            { name: 'Dashboard', path: '/dashboard', icon: FiGrid, roles: ['USER', 'ADM', 'MASTER'] },
            { name: 'Triagem de Pedidos', path: '/pedidos/triagem', icon: FiBox, roles: ['ADM', 'MASTER'], hasOrderBadge: true },
            { name: 'Gestão de Pedidos', path: '/pedidos/gestao', icon: FiTruck, roles: ['USER', 'ADM', 'MASTER'] },
            { name: 'Pedidos Concluídos', path: '/pedidos/concluidos', icon: FiArchive, roles: ['USER', 'ADM', 'MASTER'] }
        ]
    },
    {
        category: 'Leads',
        items: [
            { name: 'Leads Novos', path: '/leads/novo', icon: FiTarget, roles: ['ADM', 'MASTER'], hasLeadBadge: true },
            { name: 'Gestão de Leads', path: '/leads/gestao', icon: FiActivity, roles: ['USER', 'ADM', 'MASTER'] },
            { name: 'Clientes Elite', path: '/leads/clientes', icon: FiAward, roles: ['USER', 'ADM', 'MASTER'] }
        ]
    },
    {
        category: 'Gestão de Planos',
        items: [
            { name: 'Cadastrar Banners', path: '/banners/cadastrar', icon: FiImage, roles: ['ADM', 'MASTER'] },
            { name: 'Cadastrar Plano', path: '/imoveis/cadastrar', icon: FiPlusCircle, roles: ['USER', 'ADM', 'MASTER'] },
            { name: 'Aprovação de Planos', path: '/imoveis/aprovacao', icon: FiClock, roles: ['USER', 'ADM', 'MASTER'] },
            { name: 'Planos Publicados', path: '/imoveis/publicados', icon: FiDatabase, roles: ['USER', 'ADM', 'MASTER'] }
        ]
    },
    {
        category: 'Usuários e Configurações',
        items: [
            { name: 'Gestão de Usuários', path: '/user/management', icon: FiUsers, roles: ['ADM', 'MASTER'] },
            { name: 'Triagem de Recrutas', path: '/user/approval', icon: FiUserPlus, roles: ['ADM', 'MASTER'], hasUserBadge: true },
            { name: 'Segurança Sniper', path: '/sniper/security', icon: FiShield, roles: ['ADM', 'MASTER'] },
            { name: 'Vínculos/API', path: '/config/vinculos', icon: FiLink, roles: ['ADM', 'MASTER'] },
            { name: 'Configurações', path: '/configuracoes', icon: FiSettings, roles: ['ADM', 'MASTER'] }
        ]
    }
];

const NavItem = ({ to, icon: Icon, children, mode, badgeCount }) => {
    const isFull = mode === 2;
    const isMini = mode === 1;

    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                `flex items-center py-3 rounded-xl transition-all duration-300 relative group mb-1 ${
                    isMini ? 'px-0 justify-center' : 'px-4 mx-2'
                } ${isActive ? 'bg-[var(--accent-color)] text-black shadow-[0_0_20px_rgba(var(--accent-rgb),0.3)]' : 'text-[var(--text-secondary)] hover:bg-white/5 hover:text-[var(--text-primary)]'}`
            }
        >
            <Icon size={20} className="flex-shrink-0 transition-transform group-hover:scale-110" />
            {badgeCount > 0 && (
                <span className={`absolute bg-red-600 text-white font-black rounded-full flex items-center justify-center animate-pulse border-2 border-black z-10 ${isFull ? 'top-3 right-4 h-5 w-5 text-[9px]' : 'top-1 right-1 h-3.5 w-3.5 text-[7px]'}`}>
                    {badgeCount}
                </span>
            )}
            <span className={`ml-4 font-black uppercase text-[10px] tracking-[0.12em] transition-all duration-500 whitespace-nowrap flex-1 overflow-visible ${isFull ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none absolute'}`}>
                {children}
            </span>
        </NavLink>
    );
};

export default function Sidebar({ mode }) {
    const navigate = useNavigate();
    const [userRole, setUserRole] = useState(null);
    const [counts, setCounts] = useState({ users: 0, leads: 0, orders: 0 });
    
    // Novos estados para o modo Vivo
    const [isHovered, setIsHovered] = useState(false);
    const [isPinned, setIsPinned] = useState(() => {
        return localStorage.getItem('sidebar_pinned') === 'true';
    });

    const togglePin = () => {
        const next = !isPinned;
        setIsPinned(next);
        localStorage.setItem('sidebar_pinned', next);
    };

    // Se o mode for 0 (oculto pelo MainLayout), mantém 0. 
    // Senão, é 2 se estiver pinado ou com hover, caso contrário 1 (mini).
    const effectiveMode = mode === 0 ? 0 : (isPinned || isHovered ? 2 : 1);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return setUserRole('GUEST');

        try {
            const decoded = jwtDecode(token);
            const role = (decoded.role || decoded.authType || 'GUEST').toUpperCase();
            setUserRole(role);
            
            console.log(`🛰️ [SISTEMA]: Operador ${role} no posto.`);

            if (role === 'MASTER' || role === 'ADM') {
                // 🛠️ EXECUÇÃO PROTEGIDA: Tenta carregar, mas não deixa crashar se a função não existir ou a API falhar
                const loadBadges = async () => {
                    try {
                        const [u, l, o] = await Promise.allSettled([
                            UserExecutionEngine?.listarQuarentena?.(),
                            LeadExecutionEngine?.listarPorStatus?.('NOVO'),
                            OrderExecutionEngine?.listarNovos?.()
                        ]);

                        setCounts({
                            users: u.status === 'fulfilled' && Array.isArray(u.value) ? u.value.length : 0,
                            leads: l.status === 'fulfilled' && Array.isArray(l.value) ? l.value.length : 0,
                            orders: o.status === 'fulfilled' && Array.isArray(o.value) ? o.value.length : 0
                        });
                    } catch (err) { console.warn("🛰️ [SINAL FRACO]: Sensores de badges offline."); }
                };
                loadBadges();
            }
        } catch (e) {
            console.error("🚩 Erro crítico no decodificador:", e);
            setUserRole('GUEST');
        }
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    const sidebarWidth = effectiveMode === 0 ? 'w-0 -translate-x-full' : effectiveMode === 1 ? 'w-24' : 'w-72';

    return (
        <aside 
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`flex-shrink-0 ${sidebarWidth} border-r border-[var(--border-color)] transition-all duration-500 ease-in-out z-[100] h-screen sticky top-0 flex flex-col bg-[var(--bg-card)] overflow-hidden`}
        >
            {/* LOGO */}
            <div className={`flex items-center h-24 border-b border-[var(--border-color)] ${effectiveMode === 1 ? 'justify-center px-0' : 'px-6'}`}>
                <div className="relative">
                    <img src="/logo.png" alt="Logo" className="h-11 w-11 rounded-full shadow-[0_0_20px_rgba(var(--accent-rgb),0.2)]" />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-black rounded-full"></div>
                </div>
                <div className={`ml-4 transition-all duration-500 ${effectiveMode === 2 ? 'opacity-100' : 'opacity-0 w-0 hidden'}`}>
                    <span className="text-xl font-black text-white tracking-tighter block leading-none">UNITY</span>
                    <span className="text-[8px] block font-black tracking-[0.4em] uppercase text-[var(--accent-color)] mt-1">TIM Edition</span>
                </div>
            </div>
            
            {/* NAVEGAÇÃO */}
            <nav className="flex-1 py-6 space-y-4 overflow-y-auto no-scrollbar">
                {navGroups.map((group, groupIdx) => {
                    const filteredItems = group.items.filter(item => {
                        if (!userRole) return false;
                        if (userRole === 'MASTER') return true;
                        return item.roles.includes(userRole);
                    });

                    if (filteredItems.length === 0) return null;

                    return (
                        <div key={groupIdx} className="flex flex-col">
                            {/* NOME DA CATEGORIA */}
                            <div className={`px-6 mb-2 transition-all duration-500 ${effectiveMode === 2 ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
                                <span className="text-[9px] font-black uppercase text-[var(--text-secondary)] opacity-50 tracking-[0.2em]">{group.category}</span>
                            </div>
                            
                            {/* ITEMS */}
                            <div className="space-y-1">
                                {filteredItems.map(item => (
                                    <NavItem 
                                        key={item.name} 
                                        to={item.path} 
                                        icon={item.icon} 
                                        mode={effectiveMode}
                                        badgeCount={
                                            item.hasUserBadge ? counts.users : 
                                            item.hasLeadBadge ? counts.leads : 
                                            item.hasOrderBadge ? counts.orders : 0
                                        }
                                    >
                                        {item.name}
                                    </NavItem>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </nav>

            {/* RODAPÉ */}
            <div className={`p-4 border-t border-[var(--border-color)] bg-black/20 flex flex-col gap-2`}>
                <div className={`bg-black/40 rounded-2xl border border-white/5 p-4 transition-all ${effectiveMode === 1 ? 'hidden' : 'block'}`}>
                    <p className="text-[8px] font-black uppercase text-white/30 tracking-widest leading-none mb-1">Patente Ativa</p>
                    <p className="text-[11px] font-bold text-[var(--accent-color)] uppercase truncate">
                        {userRole === 'MASTER' ? '⚡ Master Command' : userRole || 'Identificando...'}
                    </p>
                </div>
                
                <div className="flex gap-2">
                    <button onClick={handleLogout} className={`flex-1 flex items-center py-4 rounded-2xl text-[var(--text-secondary)] hover:bg-red-500/10 hover:text-red-500 transition-all ${effectiveMode === 1 ? 'justify-center' : 'px-4'}`}>
                        <FiLogOut size={22} />
                        <span className={`ml-4 text-[11px] font-black uppercase tracking-[0.2em] ${effectiveMode === 2 ? 'block' : 'hidden'}`}>Sair</span>
                    </button>

                    {effectiveMode === 2 && (
                        <button 
                            onClick={togglePin} 
                            className={`flex items-center justify-center p-4 rounded-2xl border transition-all ${isPinned ? 'border-[var(--accent-color)] text-[var(--accent-color)] bg-[var(--accent-color)]/10' : 'border-white/10 text-white/50 hover:bg-white/5 hover:text-white'}`} 
                            title={isPinned ? "Desfixar Menu" : "Fixar Menu"}
                        >
                            {isPinned ? <FiLock size={18} /> : <FiUnlock size={18} />}
                        </button>
                    )}
                </div>
            </div>
        </aside>
    );
}